import React from 'react';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';

// Icono por tipo de ubicación (coherente con la vista aérea de Ubicaciones).
export function locationIcon(kind) {
  switch (kind) {
    case 'principal':
    case 'sub_almacen':
      return '🏭';
    case 'nevera':
      return '🧊';
    case 'armario':
      return '🗄️';
    default:
      return '📦';
  }
}

/**
 * Mini-diálogo "¿De dónde sale?" — aparece SOLO cuando hay ambigüedad (el producto
 * está en 2+ ubicaciones de sala) o falta stock en la designada. El operario elige
 * la ubicación; el resto de ventas no muestran nada (regla de oro automática).
 */
export default function LocationChoiceModal({ active, onClose, detail, onChoose, processing, unitType }) {
  // Antes del early return: un hook no puede llamarse condicionalmente.
  const overlayDismiss = useOverlayDismiss(onClose);
  if (!active || !detail) return null;
  const { product_name, reason, needed, options = [] } = detail;
  const unit = unitType === 'weight' ? 'gr' : 'u';
  const fmtQty = (q) => {
    const n = Number(q);
    if (!Number.isFinite(n)) return q;
    return n.toLocaleString('en-GB', { maximumFractionDigits: 3 });
  };
  const isAmbiguous = reason === 'ambiguous';
  const title = isAmbiguous ? '¿De dónde sale?' : 'No hay suficiente ahí';
  const subtitle = isAmbiguous
    ? `${product_name} está en varias ubicaciones. Elige de cuál descontar.`
    : `${product_name}: faltan unidades en la ubicación. Elige de dónde descontar ${needed}.`;

  return (
    <div
      className="fixed inset-0 z-[60] overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
      {...overlayDismiss}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
       
      >
        <div className="bg-gradient-to-r from-purple-700 to-purple-900 rounded-t-2xl px-6 py-4 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold">{title}</h2>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
          </div>
          <p className="text-purple-200 text-sm mt-1">{subtitle}</p>
        </div>

        <div className="p-4 space-y-2">
          {options.length === 0 && (
            // Sin opciones NO es un callejón sin salida: un modal sin ninguna acción no debería
            // existir. Ofrecer continuar sin elegir ubicación (onChoose(null) → el backend la
            // decide) o cancelar. El camino normal ni siquiera abre este modal (auto-sanado),
            // pero esto es la red por si un backend viejo aún manda options: [].
            <div className="space-y-3">
              <p className="py-2 text-center text-sm text-[#737373]">
                No hay ninguna ubicación con stock para elegir.
              </p>
              <button
                type="button"
                disabled={processing}
                onClick={() => onChoose(null)}
                className="w-full rounded-xl border border-purple-400 bg-purple-50 px-4 py-3 text-sm font-medium text-purple-800 hover:bg-purple-100 transition-colors disabled:opacity-60"
              >
                Continuar sin elegir ubicación
              </button>
              <button
                type="button"
                disabled={processing}
                onClick={onClose}
                className="w-full rounded-xl border border-[#E5E5E5] px-4 py-3 text-sm font-medium text-[#525252] hover:bg-[#FAFAFA] transition-colors disabled:opacity-60"
              >
                Cancelar
              </button>
            </div>
          )}
          {options.map((opt) => {
            const enough = opt.enough;
            return (
              <button
                key={opt.location_id}
                type="button"
                disabled={!enough || processing}
                onClick={() => onChoose(opt.location_id)}
                className={`w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                  enough
                    ? 'border-[#E5E5E5] hover:border-purple-400 hover:bg-purple-50 cursor-pointer'
                    : 'border-[#F0F0F0] bg-[#FAFAFA] cursor-not-allowed opacity-60'
                }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-lg" aria-hidden>{locationIcon(opt.kind)}</span>
                  <span className="truncate text-sm font-medium text-[#0A0A0A]">{opt.name}</span>
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm tabular-nums text-[#525252]">Stock {fmtQty(opt.quantity)} {unit}</span>
                  {!enough && <span className="text-[10px] font-semibold uppercase text-[#DC2626]">no llega</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
